import React, { useState } from 'react';
import { Button, notification, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Content } from 'antd/lib/layout/layout';

import { useAppDispatch, useAppSelector } from 'store';
import { MainLayout } from 'components/Layout/MainLayout';
import { useQuery } from 'hooks/useQuery';
import { useDeepEffect } from 'utils/useDeepEffect';
import { StickyConfirmModal } from 'components/modals/StickyConfirmModal';
import {
  deleteVacanciesItem,
  loadVacanciesList,
  setUnPublishVacancy,
} from 'store/slices/vacancies/actions';
import { PageTitles } from 'constants/pageTitles';
import { IVacanciesItemResponse } from 'store/slices/vacancies/interfaces';
import { canBeUnPublished } from 'utils/rights';
import { StatusesEnum } from 'constants/status';
import { LangEnum } from 'constants/lang';

import { Filters } from './components/Filters';
import { ListTable } from './components/ListTable';

export const List = () => {
  const [sort, setSort] = React.useState(null);
  const dispatch = useAppDispatch();
  const query = useQuery();

  const { list, total, limit } = useAppSelector(state => state.vacancies);
  const { b2bUrl } = useAppSelector(state => state.configSlice);

  const loadVacancies = () => {
    dispatch(
      loadVacanciesList({
        ...query,
        sort,
      })
    );
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<IVacanciesItemResponse | null>(
    null
  );

  useDeepEffect(() => {
    loadVacancies();
  }, [query, sort]);

  useDeepEffect(() => {
    window.scrollTo(0, 0);
  }, [query.offset]);

  const modalCloseHandler = () => {
    setModalIsOpen(false);
    setCurrentItem(null);
  };

  const handleDelete = async id => {
    await dispatch(deleteVacanciesItem({ id }))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Вакансия успешно удалена',
        });
        loadVacancies();
        modalCloseHandler();
      });
  };

  const handleUnPublish = async id => {
    await dispatch(setUnPublishVacancy(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Вакансия успешно снята с публикации',
        });
        loadVacancies();
        modalCloseHandler();
      });
  };

  const getActions = (item: IVacanciesItemResponse) => [
    (item.status === StatusesEnum.PUBLISHED ||
      item.status === StatusesEnum.NOT_PUBLISHED) && {
      key: 'responds',
      label: <Link to={`/vacancies/${item.id}/responds`}>Отклики</Link>,
    },
    item.status === StatusesEnum.PUBLISHED && {
      key: 'website',
      label: (
        <a
          href={`${b2bUrl}/${item.lang}/vacancies/${item.id}`}
          target='_blank'
          rel='noreferrer nofollow'
        >
          На сайте
        </a>
      ),
    },
    {
      key: 'edit',
      label: <Link to={`/vacancies/${item.id}/edit`}>Редактировать</Link>,
    },
    item.lang === LangEnum.ru && {
      key: 'addTranslation',
      label: (
        <Link to={`/vacancies/${item.id}/add-translation`}>
          Создать копию для перевода
        </Link>
      ),
    },
    canBeUnPublished(item) && {
      key: 'unPublish',
      label: <Typography.Text>Снять с публикации</Typography.Text>,
      onClick: () => handleUnPublish(item.id),
    },
    {
      key: 'remove',
      label: <Typography.Text type='danger'>Удалить</Typography.Text>,
      onClick: () => {
        setModalIsOpen(true);
        setCurrentItem(item);
      },
    },
  ];

  return (
    <MainLayout
      pageTitle={PageTitles.vacancies}
      extraButtonsList={[
        <Link key={1} to='/vacancies/add'>
          <Button type='primary'>Создать</Button>
        </Link>,
      ]}
      aside={<Filters />}
    >
      <Content>
        <ListTable
          data={list}
          total={total}
          getActions={getActions}
          limit={limit}
          setSort={setSort}
        />
      </Content>
      {currentItem?.id && (
        <>
          {modalIsOpen && (
            <StickyConfirmModal
              isOpen={modalIsOpen}
              onRequestClose={modalCloseHandler}
              title='Удаление вакансии'
              okText='Удалить'
              text={
                <div>
                  Вы хотите убрать с портала и переместить в Корзину вакансию{' '}
                  <Typography.Text strong>
                    {currentItem.name || ''}
                  </Typography.Text>
                  ?
                </div>
              }
              onSubmit={() => handleDelete(currentItem.id)}
              okButtonProps={{ danger: true }}
            />
          )}
        </>
      )}
    </MainLayout>
  );
};
